import React from 'react';
import { Button, Tile } from '@carbon/react';
import { Checkmark, Star } from '@carbon/icons-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useAudUsdFxRate } from '../../hooks/useFxRate';
import { supabase } from '../../services/supabaseClient';

const PricingTier = ({ tier }) => {
  const { theme } = useTheme();
  const { user, isAuthenticated, session } = useAuth();
  const { subscription, hasActiveSubscription, refreshSubscription } = useSubscription();
  const { rate: audUsdRate } = useAudUsdFxRate();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  const accentColor = '#0f62fe';

  const isYearly = tier.billingInterval === 'yearly';

  const usdApprox =
    typeof audUsdRate === 'number' && Number.isFinite(audUsdRate)
      ? tier.basePrice * audUsdRate
      : null;

  const usdParen = (audAmount, { maximumFractionDigits = 0 } = {}) => {
    if (typeof audUsdRate !== 'number' || !Number.isFinite(audUsdRate)) return '';
    if (typeof audAmount !== 'number' || !Number.isFinite(audAmount)) return '';
    const usd = audAmount * audUsdRate;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits,
    }).format(usd);
    return ` (${formatted} USD)`;
  };

  // Determine button state and text based on subscription
  const getButtonState = () => {
    // No active subscription - all plans available, all primary (blue) CTAs
    const isActive = hasActiveSubscription && subscription?.status === 'active';
    if (!isActive) {
      return {
        disabled: false,
        text: tier.cta,
        kind: 'primary',
        tooltip: null,
        isCurrent: false,
      };
    }

    // Has active subscription - check upgrade/downgrade logic
    // Use the raw plan key (e.g. 'free') not planName ('Free Plan').
    const currentPlan = (subscription?.plan || subscription?.planName || '').toLowerCase().replace(/\s+plan$/, '');
    const tierName = tier.name.toLowerCase();
    
    const planHierarchy = {
      'free': 0,
      'starter': 1,
      'business': 2,
      'enterprise': 3
    };

    const currentLevel = planHierarchy[currentPlan] || 0;
    const tierLevel = planHierarchy[tierName] || 0;

    if (tierLevel < currentLevel) {
      // Lower tier - downgrade available
      return {
        disabled: false,
        text: 'Downgrade',
        kind: 'ghost',
        tooltip: `Downgrade from ${currentPlan} to ${tierName}. Changes will take effect next billing cycle.`,
        isCurrent: false,
      };
    } else if (tierLevel === currentLevel) {
      // Current tier - already subscribed
      return {
        disabled: true,
        text: 'Current Plan',
        kind: 'primary',
        tooltip: 'You are currently on this plan',
        isCurrent: true,
      };
    } else {
      // Higher tier - upgrade available
      return {
        disabled: false,
        text: 'Upgrade',
        kind: 'primary',
        tooltip: `Upgrade from ${currentPlan} to ${tierName}`,
        isCurrent: false,
      };
    }
  };

  const buttonState = getButtonState();

  const handleGetStarted = async () => {
    if (tier.name === 'Enterprise') {
      // Navigate to contact sales for Enterprise
      window.location.href = '/contact';
      return;
    }

    try {
      // Read the token at click time — React context `session` can be stale/null
      // even when `user` is set, which skipped free-plan activation and sent
      // people to Stripe Checkout with an empty userId (no return confirmation).
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || session?.access_token || null;

      if (!accessToken) {
        window.location.href = `/signup?plan=${tier.name.toLowerCase()}`;
        return;
      }

      const requestData = {
        isSubscription: true,
        plan: tier.name.toLowerCase(),
        billingInterval: tier.billingInterval || 'monthly',
        userId: user?.id || sessionData?.session?.user?.id || null,
        total: tier.basePrice,
        shippingInfo: { email: user?.email || sessionData?.session?.user?.email },
      };
      
      console.log('Sending to Stripe function:', requestData);
      
      const response = await fetch('/.netlify/functions/stripe-create-subscription-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestData),
      });

      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        if (!isAuthenticated) window.location.href = `/signup?plan=${tier.name.toLowerCase()}`;
        return;
      }
      const data = await response.json();
      const redirectUrl = data.session?.url ?? data.sessionUrl;

      if (!response.ok) {
        console.error('Stripe function error:', { status: response.status, error: data.error, requestData });
        alert(data?.error || `Checkout failed (${response.status}). Please try again.`);
      }

      if (response.ok && data.swapped) {
        // Plan was updated in place (no Checkout redirect). Refresh + confirm.
        if (typeof refreshSubscription === 'function') refreshSubscription();
        alert(data.message || 'Your plan has been updated.');
        window.location.href = '/dashboard/subscription';
        return;
      }

      if (response.ok && redirectUrl) {
        window.location.href = redirectUrl;
      } else if (response.ok) {
        if (data?.error) console.error('Error creating subscription session:', data.error);
        if (!isAuthenticated) window.location.href = `/signup?plan=${tier.name.toLowerCase()}`;
      }
    } catch (error) {
      if (!error.message?.includes('Unexpected token')) console.error('Subscription error:', error);
      if (!isAuthenticated) window.location.href = `/signup?plan=${tier.name.toLowerCase()}`;
    }
  };

  return (
    <Tile 
      className="pricing-tier-card"
      style={{
        backgroundColor: buttonState.isCurrent
          ? (isDark ? '#0f2417' : '#f0fdf4')
          : tier.popular ? (isDark ? '#1a1a1a' : '#f8f9fa') : bgPrimary,
        border: buttonState.isCurrent
          ? '2px solid #24a148'
          : tier.popular ? `2px solid ${accentColor}` : `1px solid ${borderColor}`,
        borderRadius: '12px',
        padding: '24px 20px',
        position: 'relative',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        height: '100%',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = isDark 
          ? '0 12px 24px rgba(0, 0, 0, 0.4)' 
          : '0 12px 24px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Popular Badge */}
      {tier.popular && !buttonState.isCurrent && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: accentColor,
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <Star size={12} />
          MOST POPULAR
        </div>
      )}

      {/* Current Plan Badge */}
      {buttonState.isCurrent && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#24a148',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
        }}>
          <Checkmark size={12} />
          YOUR CURRENT PLAN
        </div>
      )}

      {/* Plan Name */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          marginBottom: '8px',
          color: tier.popular ? accentColor : textPrimary
        }}>
          {tier.name}
        </h3>
        <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>
          {tier.description}
        </p>
      </div>

      {/* Price — use slash units (e.g. AUD / month, AUD / year) so copy matches common pricing style */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        {tier.free ? (
          <div className="pricing-tier-price" style={{ fontSize: '2.25rem', fontWeight: '700', color: textPrimary, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            Free
          </div>
        ) : (
        <div className="pricing-tier-price" style={{ fontSize: '2.25rem', fontWeight: '700', color: textPrimary, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
          <span>${tier.basePrice}</span>
          <span style={{ fontSize: '1.25rem', fontWeight: 600, color: textSecondary }}> AUD</span>
          <span style={{ fontSize: '1.25rem', fontWeight: 600, color: textSecondary }}>
            {tier.priceUnitSuffix != null && tier.priceUnitSuffix !== ''
              ? (String(tier.priceUnitSuffix).startsWith(' ') ? tier.priceUnitSuffix : ` ${tier.priceUnitSuffix}`)
              : isYearly
                ? ' / year'
                : ' / month'}
          </span>
          {usdApprox !== null && (
            <span style={{ fontSize: '1rem', fontWeight: 600, color: textSecondary }}>
              {' '}
              (
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(usdApprox)}{' '}
              USD)
            </span>
          )}
        </div>
        )}
        {isYearly && tier.monthlyEquivalent && (
          <div style={{ color: '#24a148', fontSize: '0.875rem', fontWeight: 500, marginTop: '8px' }}>
            ${tier.monthlyEquivalent} AUD / month{usdParen(tier.monthlyEquivalent)} — save 20%
          </div>
        )}
      </div>

      {/* Sensor & Gateway Capacity */}
      <div style={{
        backgroundColor: isDark ? '#262626' : '#f4f4f4',
        padding: '16px',
        borderRadius: '8px',
        textAlign: 'center',
        marginBottom: '32px'
      }}>
        <div style={{ fontSize: '1.125rem', fontWeight: '500', color: textPrimary }}>
          {tier.free
            ? `Connect 1 sensor and 1 gateway`
            : tier.gatewayCount === 'unlimited'
              ? `Connect ${tier.sensorCapacity} sensors · unlimited gateways`
              : `Connect up to ${tier.sensorCapacity} sensors`}
        </div>
        {!tier.free && (
          <div style={{ fontSize: '0.875rem', color: textSecondary, marginTop: '4px' }}>
            Add more sensors for $5 AUD / sensor / month{usdParen(5)}
          </div>
        )}
        {tier.gatewayLabel && !tier.free && tier.gatewayCount !== 'unlimited' && (
          <div style={{ fontSize: '0.875rem', color: textSecondary, marginTop: '4px' }}>
            {tier.gatewayLabel}
          </div>
        )}
      </div>

      {/* Features */}
      <div style={{ flex: 1, marginBottom: '32px' }}>
        <ul style={{ 
          listStyle: 'none', 
          padding: 0, 
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {tier.features.map((feature, index) => (
            <li key={index} style={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              gap: '12px',
              color: textPrimary,
              fontSize: '0.95rem'
            }}>
              <div style={{
                backgroundColor: accentColor,
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                <Checkmark size={12} />
              </div>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA Button */}
      <div style={{ position: 'relative' }}>
        <Button
          kind={buttonState.kind}
          size="lg"
          disabled={buttonState.disabled}
          style={{ 
            width: '100%',
            maxWidth: '100%',
            fontWeight: '600',
            padding: '14px 16px',
            whiteSpace: 'normal',
            height: 'auto',
            minHeight: '3rem',
            opacity: buttonState.disabled ? 0.6 : 1,
            cursor: buttonState.disabled ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={!buttonState.disabled ? handleGetStarted : undefined}
          title={buttonState.tooltip}
        >
          {buttonState.text}
        </Button>
        {buttonState.tooltip && (
          <div style={{
            position: 'absolute',
            top: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#262626',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            zIndex: 10,
            opacity: buttonState.disabled ? 1 : 0,
            pointerEvents: 'none'
          }}>
            {buttonState.tooltip}
            <div style={{
              position: 'absolute',
              bottom: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid #262626'
            }} />
          </div>
        )}
      </div>

      {/* Additional Info */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '16px',
        fontSize: '0.875rem',
        color: textSecondary
      }}>
        {tier.name === 'Enterprise' ? (
          <span>Custom pricing available</span>
        ) : tier.free ? (
          <span>No credit card required</span>
        ) : (
          <span>Cancel anytime</span>
        )}
      </div>
    </Tile>
  );
};

export default PricingTier;
